export interface LivingReportDefaultSkillSpec {
  name: string;
  displayName: string;
  description: string;
  requiredTools: string[];
  instructions: string;
}

export const LIVING_REPORT_BUILDER_SKILL_ID = 'system:living-report-builder-agent';

export const LIVING_REPORT_DEFAULT_SKILL_SPECS: LivingReportDefaultSkillSpec[] = [
  {
    name: 'living-report-evidence-editor',
    displayName: 'Doc Evidence Editor',
    description:
      'Use when creating an analytical Attune Doc to keep every claim traceable, current, and appropriately qualified.',
    requiredTools: [],
    instructions: `Treat evidence fidelity as the first constraint.

- Re-check time-sensitive claims with the available Attune data tools.
- Never invent numbers, dates, trends, comparisons, or sources.
- Distinguish observed facts, interpretations, and recommendations.
- Preserve uncertainty and state material coverage or data limitations.
- Cite the strongest available source near each decision-relevant claim.`,
  },
  {
    name: 'living-report-change-analyst',
    displayName: 'Doc Change Analyst',
    description:
      'Use when comparing a recurring Attune Doc with earlier work while preserving still-valid context and structure.',
    requiredTools: [],
    instructions: `Analyze the new evidence against the current report, not as a blank-page rewrite.

- Preserve stable sections and conclusions that remain supported.
- Call out material changes, their likely drivers, and why they matter.
- Avoid manufacturing a trend from a single observation or incompatible periods.
- Retain useful historical context while clearly labeling the current state.
- End with concrete implications or actions only when the evidence supports them.`,
  },
  {
    name: 'living-report-visual-designer',
    displayName: 'Doc Visual Designer',
    description:
      'Use when turning an Attune answer into a polished report, article, brief, or other Attune Doc with clear hierarchy and truthful widgets.',
    requiredTools: [],
    instructions: `Shape the answer into a concise, self-contained analytical report.

- Lead with the decision-relevant takeaway, then evidence, implications, and actions.
- Use KPI cards only for explicit headline metrics.
- Use charts only when complete comparable numeric rows are available; otherwise use a table or prose.
- Keep headings and section order stable across refreshes unless new evidence requires a structural change.
- Give each section a short heading, followed by short paragraphs or real list blocks. Use heading levels 1–3 in order; never simulate headings with bold paragraphs.
- Use inline Markdown for emphasis, links, and code in prose block text. List items and quotes support Markdown paragraphs and nested lists. Keep code_block text and widget values literal.
- Do not wrap a whole Doc in a code fence, place an entire Markdown document inside one paragraph block, or insert HTML for styling.
- Prefer a few informative widgets over decorative visualization or generic filler.`,
  },
];

export const LIVING_REPORT_BUILDER_SKILL_SPEC: LivingReportDefaultSkillSpec = {
  name: 'living-report-builder-agent',
  displayName: 'Attune Doc Builder',
  description:
    'Create, edit, and polish reports, articles, briefs, and recurring outputs as Attune Docs directly from Notebook or background-agent work.',
  requiredTools: [
    'docs.list',
    'docs.get',
    'docs.create',
    'docs.update',
    'docs.apply_operations',
    'docs.versions',
    'docs.restore',
    'docs.schedule',
    'docs.refresh',
    'docs.archive',
  ],
  instructions: `Build and manage Attune Docs directly through the docs.* tools. A Doc may be an analytical report, article, research brief, plan, or recurring dated output. You are the document builder; do not hand the answer to another agent or compiler.

- When turning a Notebook reply into a Doc, retrieve the exact message if needed and preserve all substantive content, hierarchy, metrics, tables, charts, citations, and caveats. For prose articles, favor readable headings, paragraphs, lists, callouts, and sources; never force analytical widgets where they do not help.
- For creation, call docs.create with a complete ordered blocks array. Choose the appropriate typed component for each section and give every block a durable semantic blockId.
- Author only the public Attune blocks schema. Never emit or discuss private editor JSON; the server handles that conversion.
- Create the complete Doc in one docs.create call. Never create a small shell and expand it to work around a server error. If creation is explicitly rejected by validation, correct the reported fields and retry the complete Doc once. After a timeout or uncertain result, inspect docs.list and docs.get for the intended Doc before retrying; never blindly duplicate a possibly successful create. Report unresolved failure honestly.
- If the source message already confirms a Doc was created and contains /app/docs/{reportId}, open or return that existing Doc. Do not dereference it and create a duplicate.
- A direct user command or a trusted background-task instruction to create a Doc is already authorization. Do not request another confirmation before calling docs.create, docs.update, or docs.apply_operations.
- For a recurring or background-task Doc, create a new Doc on every run. Include the run date in the title and never overwrite an earlier run's Doc.
- Edit an existing Doc only when explicitly asked. Read it first with docs.get, then use docs.apply_operations with the exact currentVersion; use docs.update only for a deliberate complete-body replacement.
- When the runtime identifies an active Doc beside the chat, resolve "this doc", "the open report", and similar references to that reportId. Do not switch targets unless the user explicitly identifies another Doc.
- Put every intended block change into one docs.apply_operations call. If a version conflict occurs, re-read and retry once only; after a second conflict, stop and report it. Never loop on Doc reads/writes.
- If docs.get shows populated chart or table data but the UI looks empty, do not rewrite or sanitize the content. Report a renderer defect; repeated content edits cannot repair a display bug.
- Use docs.versions, docs.restore, docs.schedule, docs.refresh, or docs.archive only when the user explicitly asks for that lifecycle action.
- After a successful create or edit, return the Doc title and a link in the form /app/docs/{reportId}.`,
};

export function renderLivingReportBuilderSkillInstructions(): string {
  const presentationSkills = LIVING_REPORT_DEFAULT_SKILL_SPECS.filter(
    skill =>
      skill.name === 'living-report-change-analyst' ||
      skill.name === 'living-report-visual-designer'
  );
  return [...presentationSkills, LIVING_REPORT_BUILDER_SKILL_SPEC]
    .map(skill => `### ${skill.displayName}\n${skill.instructions}`)
    .join('\n\n');
}
